@mixin chip--size-default() {
  --font-size: var(--stzh-font-micro-font-size);
  --line-height: var(--stzh-font-micro-text-line-height);
  --letter-spacing: normal;

  --height: 32px;
  --outer-height: 40px;
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-left: #{space('small')};
  --padding-right: #{space('small')};
  --border-radius: 16px;

  @include mq($from: medium) {
    --outer-height: auto;
  }
}

@mixin chip--size-large() {
  --height: 40px;
  --border-radius: 20px;
  --padding-left: #{space('medium')};
  --padding-right: #{space('medium')};
}

@mixin chip--size-small() {
  --font-size: var(--stzh-font-nano-font-size);
  --line-height: var(--stzh-font-nano-text-line-height);
  --letter-spacing: normal;

  --height: 24px;
  --border-radius: 12px;
  --padding-left: #{space('xsmall')};
  --padding-right: #{space('xsmall')};
}

:host {
  @include chip--size-default;
  --color: var(--stzh-chip-color, #{$colorPrimary70});
  --remove-color: var(--color);
  --remove-border-radius: 24px;
  --background-color: var(--stzh-chip-background-color, #{$colorSecondary30});
  --icon-size: #{iconSize('xsmall')};
  --icon-color: var(--color);
  --icon-gap: #{space('xsmall')};
  --counter-color: var(--color);
  --counter-gap: #{space('xsmall')};

  --hover-color: var(--stzh-chip-hover-color, #{$colorPrimary70});
  --hover-background-color: var(--stzh-chip-hover-background-color, #{$colorSecondary40});
  --hover-remove-color: var(--hover-color);
  --hover-remove-background-color: #{$colorPrimary30};
  --hover-icon-color: var(--hover-color);
  --hover-counter-color: var(--hover-color);

  display: inline-flex;

  @include highContrast() {
    --hover-color: Highlight;
    --hover-background-color: Canvas;
    --hover-remove-background-color: Canvas;
  }

  &[variant="input"] {
    --background-color: #{$colorPrimary10};

    --hover-color: #{$colorPrimary90};
    --hover-background-color: #{$colorPrimary20};
  }

  &[variant="tag"] {
    --color: #{$colorGrey90};
    --background-color: #{$colorGrey20};

    --hover-color: #{$colorGrey90};
    --hover-background-color: #{$colorGrey20};
  }

  &[variant="input"]:where([size="small"]) {
    --icon-gap: #{space('xxsmall')};
  }

  &[variant="tag"]:where([size="small"]) {
    --padding-left: #{space('xsmall')};
    --padding-right: #{space('xsmall')};
    --icon-gap: #{space('xxsmall')};
  }

  &[variant="tag"]:where([type="warning"]) {
    --color: #{$colorWarning90};

    --hover-color: #{$colorWarning90};
  }

  &[variant="tag"]:where([type="error"]) {
    --color: #{$colorError70};

    --hover-color: #{$colorError70};
  }

  &[variant="tag"]:where([type="success"]) {
    --color: #{$colorSuccess70};

    --hover-color: #{$colorSuccess70};
  }

  &[variant="tag"]:where([inverted]:not([inverted="false"])) {
    --color: #{$colorWhite};
    --background-color: #{$colorGrey90};

    --hover-color: #{$colorWhite};
    --hover-background-color: #{$colorGrey90};
  }

  &[variant="tag"]:where([type="warning"][inverted]:not([inverted="false"])) {
    --color: #{$colorBlack};
    --background-color: #{$colorWarning60};

    --hover-color: #{$colorBlack};
    --hover-background-color: #{$colorWarning60};
  }

  &[variant="tag"]:where([type="error"][inverted]:not([inverted="false"])) {
    --background-color: #{$colorError70};

    --hover-background-color: #{$colorError70};
  }

  &[variant="tag"]:where([type="success"][inverted]:not([inverted="false"])) {
    --background-color: #{$colorSuccess70};

    --hover-background-color: #{$colorSuccess70};
  }

  &[active]:not([active="false"]) {
    --color: var(--stzh-chip-active-color, #{$colorWhite});
    --background-color: var(--stzh-chip-active-background-color, #{$colorPrimary70});

    --hover-color: var(--stzh-chip-active-hover-color, #{$colorWhite});
    --hover-background-color: var(--stzh-chip-active-hover-background-color, #{$colorPrimary90});
    --hover-remove-background-color: #{$colorPrimary90};

    @include highContrast() {
      --color: Highlight;
      --background-color: Canvas;

      --hover-color: Highlight;
      --hover-background-color: Canvas;
      --hover-remove-background-color: Canvas;
    }
  }

  &[non-interactive]:not([non-interactive="false"]) {
    --hover-color: var(--color);
    --hover-background-color: var(--background-color);
    --hover-remove-color: var(--remove-color);
    --hover-remove-background-color: transparent;
    --hover-icon-color: var(--icon-color);
    --hover-counter-color: var(--counter-color);
  }

  &[disabled]:not([disabled="false"]) {
    --color: var(--stzh-chip-disabled-color, #{$colorGrey80});
    --background-color: var(--stzh-chip-disabled-background-color, #{$colorGrey20});
    --remove-color: var(--color);
    --icon-color: var(--color);
    --counter-color: var(--color);

    --hover-color: var(--color);
    --hover-background-color: var(--background-color);
    --hover-remove-color: var(--remove-color);
    --hover-remove-background-color: transparent;
    --hover-icon-color: var(--icon-color);
    --hover-counter-color: var(--counter-color);
  }

  &[variant="input"][disabled]:not([disabled="false"]) {
    --background-color: #{$colorGrey10};
  }
}

:host(:where([size="small"])) {
  @include chip--size-small;
}

:host(:where([size="large"])) {
  @include chip--size-large;
}

@each $breakpoint, $size in $breakpoints {
  @include mq($from: $breakpoint) {
    :host(:where([size-#{$breakpoint}="default"])) {
      @include chip--size-default;
    }

    :host(:where([size-#{$breakpoint}="small"])) {
      @include chip--size-small;
    }

    :host(:where([size-#{$breakpoint}="large"])) {
      @include chip--size-large;
    }
  }
}

.stzh-chip {
  position: relative;

  &__vhidden {
    @include visuallyhidden;
  }

  &__button {
    font-size: var(--font-size);
    line-height: var(--line-height);
    letter-spacing: var(--letter-spacing);
    min-height: var(--outer-height);
    font-family: inherit;
    appearance: none;
    padding: 0px;
    border: none;
    background-color: transparent;

    &.is-button,
    &.is-link {
      cursor: pointer;
    }
  }

  &__button-inner {
    height: var(--height);
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: var(--border-radius);
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
    padding-left: var(--padding-left);
    padding-right: var(--padding-right);
    color: var(--color);
    background-color: var(--background-color);
    transition-duration: $baseTransitionAnimationSpeed;
    transition-property: color, background-color;
    text-decoration-line: none;
  }

  &__label {
    text-align: left;
  }

  &__icon-wrapper {
    --stzh-icon-size: var(--icon-size);

    display: flex;
    color: var(--icon-color);
    transition: color $baseTransitionAnimationSpeed;
    flex-shrink: 0;

    &:not(:empty) {
      margin-right: var(--icon-gap);
    }
  }

  &__counter {
    display: flex;
    font-variant-numeric: tabular-nums;
    margin-left: var(--counter-gap);
    color: var(--counter-color);
    transition: color $baseTransitionAnimationSpeed;
  }

  &__remove {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    appearance: none;
    font-family: inherit;
    border: none;
    background: none;
    padding: 0;
    width: var(--height);
    height: var(--height);
    border-radius: var(--remove-border-radius);
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: space('xxsmall');
    color: var(--remove-color);
    transition-duration: $baseTransitionAnimationSpeed;
    transition-property: color, background-color, border-color;
    cursor: pointer;

    &:hover {
      color: var(--hover-remove-color);
      background-color: var(--hover-remove-background-color);
    }
  }

  &__remove-icon {
    --size: #{iconSize('xxsmall')};
  }

  &__button:hover &__button-inner,
  &__remove:hover + &__button &__button-inner {
    color: var(--hover-color);
    background-color: var(--hover-background-color);
  }

  &__button:hover &__counter,
  &__remove:hover + &__button &__counter {
    color: var(--hover-counter-color);
  }

  &__button:hover &__icon-wrapper,
  &__remove:hover + &__button &__icon-wrapper {
    color: var(--hover-icon-color);
  }

  /* Removeable */

  &--is-removable &__button-inner {
    padding-right: calc(var(--height) + #{space('xxsmall')});
  }

  /* Disabled */

  &--is-disabled &__button,
  &--is-disabled &__remove {
    cursor: not-allowed;
  }
}
